<section class="finances">
  <header class="page-header">
    <div>
      <h1>Finances</h1>
      <p class="page-subtitle">
        Revenue, costs and profit across jobs and DIY orders
      </p>
    </div>
  </header>

  <div class="toolbar">
    <div class="tabs">
      @for (tab of tabs; track tab.value) {
        <button
          class="tab-btn"
          [class.active]="activeTab() === tab.value"
          (click)="selectTab(tab.value)"
        >
          {{ tab.label }}
        </button>
      }
    </div>

    <div class="filters">
      <label class="filter">
        <span class="filter-label">Range</span>
        <select
          class="filter-select"
          [ngModel]="range()"
          (ngModelChange)="setRange($event)"
        >
          @for (option of ranges; track option.value) {
            <option [value]="option.value">{{ option.label }}</option>
          }
        </select>
      </label>

      <label class="filter">
        <span class="filter-label">Search</span>
        <input
          type="search"
          class="filter-input"
          placeholder="Customer or order name"
          [ngModel]="search()"
          (ngModelChange)="setSearch($event)"
        />
      </label>
    </div>
  </div>

  @if (scopeLoading()) {
    <div class="loading-message">Loading finances...</div>
  } @else {
    <div class="kpi-grid">
      <div class="kpi-card accent-blue">
        <div class="kpi-label">Revenue</div>
        <div class="kpi-value">{{ kpis().revenue | currency }}</div>
      </div>

      <div class="kpi-card accent-sand">
        <div class="kpi-label">Costs</div>
        <div class="kpi-value">{{ kpis().costs | currency }}</div>
      </div>

      <div
        class="kpi-card"
        [class.accent-mint]="kpis().profit >= 0"
        [class.accent-rose]="kpis().profit < 0"
      >
        <div class="kpi-label">Profit</div>
        <div class="kpi-value" [class.negative]="kpis().profit < 0">
          {{ kpis().profit | currency }}
          <span class="kpi-delta"
            >({{ kpis().margin | number: '1.0-1' }}%)</span
          >
        </div>
        @if (kpis().unprofitable > 0) {
          <div class="kpi-foot">{{ kpis().unprofitable }} unprofitable</div>
        }
      </div>

      <div class="kpi-card accent-lavender">
        <div class="kpi-label">Paid</div>
        <div class="kpi-value">{{ kpis().paid | currency }}</div>
      </div>

      <div class="kpi-card accent-peach" [class.warn]="kpis().outstanding > 0">
        <div class="kpi-label">Outstanding</div>
        <div class="kpi-value">{{ kpis().outstanding | currency }}</div>
      </div>
    </div>

    <div class="panel">
      <div class="panel-head">
        <h2>Revenue vs profit</h2>
        <span class="panel-hint">Monthly · {{ rangeLabel() }}</span>
      </div>
      @if (!hasTrend()) {
        <p class="empty">No finance data in this range.</p>
      } @else {
        <div class="chart-canvas">
          <canvas
            baseChart
            [type]="'line'"
            [data]="trendChart()"
            [options]="trendOptions"
          ></canvas>
        </div>
      }
    </div>

    @if (activeTab() === 'diy') {
      @if (filteredDiy().length === 0) {
        <div class="empty-state">No DIY orders match these filters.</div>
      } @else {
        <div class="table-wrap">
          <table class="finance-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Customer</th>
                <th>Items</th>
                <th>Revenue</th>
                <th>Profit</th>
                <th>Status</th>
                <th>Payment</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              @for (row of filteredDiy(); track row.order.id) {
                <tr>
                  <td>{{ row.order.createdAt | date }}</td>
                  <td>
                    <div class="customer-name">
                      {{ row.order.customerName || row.order.customerEmail }}
                    </div>
                    <div class="badge badge-muted">DIY · no cost data</div>
                  </td>
                  <td>{{ row.order.items.length }}</td>
                  <td>{{ row.revenue | currency }}</td>
                  <td class="profit-positive">
                    {{ row.profit | currency }}
                    <span class="profit-percent"
                      >({{ row.profitPercent | number: '1.0-1' }}%)</span
                    >
                  </td>
                  <td>{{ row.order.status }}</td>
                  <td>{{ row.order.paymentStatus }}</td>
                  <td>
                    <a class="link-button" routerLink="/orders">View</a>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    } @else {
      @if (filteredJobs().length === 0) {
        <div class="empty-state">No jobs match these filters.</div>
      } @else {
        <div class="table-wrap">
          <table class="finance-table">
            <thead>
              <tr>
                <th class="expand-col"></th>
                <th>Customer</th>
                <th>Revenue</th>
                <th>Mfg. Cost</th>
                <th>Add. Costs</th>
                <th>Profit</th>
                <th>Paid</th>
                <th>Balance</th>
                <th>Profit Note</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              @for (row of filteredJobs(); track row.customer.id) {
                <tr
                  [class.row-negative]="row.profit < 0"
                  [class.row-no-data]="!row.hasCostData"
                  [class.row-open]="expandedKey() === row.customer.id"
                >
                  <td class="expand-col">
                    <button
                      type="button"
                      class="expand-btn"
                      [class.open]="expandedKey() === row.customer.id"
                      [attr.aria-expanded]="expandedKey() === row.customer.id"
                      [attr.aria-label]="
                        (expandedKey() === row.customer.id
                          ? 'Collapse '
                          : 'Expand ') + row.customer.name
                      "
                      (click)="toggleRow(row)"
                    >
                      <span class="chevron">›</span>
                    </button>
                  </td>
                  <td>
                    <div class="customer-name">{{ row.customer.name }}</div>
                    @if (!row.hasCostData) {
                      <div class="badge badge-muted">No cost data</div>
                    }
                    @if (row.profit < 0) {
                      <div class="badge badge-danger">Unprofitable</div>
                    }
                  </td>
                  <td>{{ row.estimate.total | currency }}</td>
                  <td>{{ row.manufacturingCost | currency }}</td>
                  <td>{{ row.additionalCosts | currency }}</td>
                  <td
                    [class.profit-negative]="row.profit < 0"
                    [class.profit-positive]="row.profit >= 0"
                  >
                    {{ row.profit | currency }}
                    <span class="profit-percent"
                      >({{ row.profitPercent | number: '1.0-1' }}%)</span
                    >
                  </td>
                  <td>{{ row.paid | currency }}</td>
                  <td
                    [class.settled-cell]="row.balance <= 0"
                    [class.balance-cell]="row.balance > 0"
                  >
                    {{
                      row.balance <= 0 ? 'Settled' : (row.balance | currency)
                    }}
                  </td>
                  <td class="note-cell">
                    @if (row.estimate.profitNote) {
                      <p class="note-text">{{ row.estimate.profitNote }}</p>
                    } @else {
                      <span class="note-empty">—</span>
                    }
                  </td>
                  <td>
                    <a
                      class="link-button"
                      [routerLink]="['/customers', row.customer.id, 'estimate']"
                    >
                      Edit
                    </a>
                  </td>
                </tr>

                @if (expandedKey() === row.customer.id) {
                  <tr class="detail-row">
                    <td colspan="10">
                      <div class="detail-panel">
                        <div class="detail-block">
                          <div class="detail-title">Cost breakdown</div>
                          <div class="cost-line">
                            <span>Manufacturing</span>
                            <strong>{{
                              row.manufacturingCost | currency
                            }}</strong>
                          </div>
                          @if (
                            (row.estimate.additionalCosts ?? []).length > 0
                          ) {
                            <div class="cost-chips">
                              @for (
                                cost of row.estimate.additionalCosts;
                                track $index
                              ) {
                                <span class="cost-chip"
                                  >{{ cost.label }}:
                                  {{ cost.amount | currency }}</span
                                >
                              }
                            </div>
                          } @else {
                            <div class="cost-empty">No additional costs</div>
                          }
                        </div>

                        <div class="detail-block">
                          <div class="detail-title">Profit note</div>
                          <textarea
                            class="note-input"
                            rows="3"
                            placeholder="What did you do with the profit?"
                            [(ngModel)]="draftNote"
                            [disabled]="savingNote()"
                          ></textarea>
                          <div class="note-actions">
                            <button
                              type="button"
                              class="note-btn note-btn-save"
                              (click)="saveNote(row)"
                              [disabled]="savingNote()"
                            >
                              {{ savingNote() ? 'Saving…' : 'Save note' }}
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                }
              }
            </tbody>
          </table>
        </div>
      }

      @if (activeTab() === 'all') {
        <div class="diy-summary-line">
          DIY orders: {{ filteredDiy().length }} · revenue
          {{ diyRevenue() | currency }}
        </div>
      }
    }
  }
</section>
